import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@chikara/ui';
import { t } from '@/i18n';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { useLogin } from '@/features/auth/hooks/useLogin';
import { companyStorage } from '@/features/auth/utils/companyStorage';
import { pickPreselectedCompany } from '@/features/auth/utils/companies';
import { trackCompanyEvent } from '@/features/auth/analytics/companyEvents';
import * as ROUTES from '@/shared/constants/routes';
import styles from './SelectCompanyPage.module.css';

export default function SelectCompanyPage() {
  const navigate = useNavigate();
  const { companies, userData, isAuthenticated } = useAuth();
  const { login, error } = useLogin();
  const email = userData.email ?? '';

  const lastCompany = useMemo(() => companyStorage.getLastCompany(email), [email]);
  const preselection = useMemo(
    () => pickPreselectedCompany(companies, lastCompany),
    [companies, lastCompany]
  );
  const [selected, setSelected] = useState(preselection.company?.customer_uuid ?? '');
  const [submitting, setSubmitting] = useState(false);
  const openedAuthenticated = useRef(isAuthenticated);

  useEffect(() => {
    if (openedAuthenticated.current) {
      navigate(ROUTES.DASHBOARD, { replace: true });
      return;
    }
    if (companies.length === 0) {
      navigate(ROUTES.LOGIN, { replace: true });
      return;
    }
    if (preselection.lastUnavailable) {
      trackCompanyEvent('company_unavailable', { customer_uuid: lastCompany });
    }
  }, [companies.length, preselection.lastUnavailable, navigate, lastCompany]);

  const handleContinue = async () => {
    if (!selected) return;
    setSubmitting(true);
    try {
      await login(selected, 'selector');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.card}>
      <h2 className={styles.title}>{t('companies.select.title')}</h2>
      <select
        className={styles.select}
        aria-label={t('companies.select.label')}
        value={selected}
        onChange={(event) => setSelected(event.target.value)}
      >
        {companies.map((company) => (
          <option key={company.customer_uuid} value={company.customer_uuid}>
            {company.company_name}
          </option>
        ))}
      </select>
      {error && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}
      <Button variant="primary" className={styles.submit} onClick={handleContinue} disabled={!selected || submitting}>
        {submitting ? t('companies.select.loading') : t('companies.select.continue')}
      </Button>
    </div>
  );
}

export { SelectCompanyPage };
